import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  ArrowLeft,
  Building2,
  HardHat,
  Loader2,
  LockKeyhole,
  LogOut,
  Mail,
  Phone,
  RefreshCw,
  Search,
  Trash2,
  Users,
} from "lucide-react";
import { Link } from "@tanstack/react-router";

import { supabase } from "@/integrations/supabase/client";

const ADMIN_EMAIL = "huzaifakhan9450@gmail.com";

interface Lead {
  id: string;
  full_name: string;
  college_name: string;
  branch: string;
  year: string;
  whatsapp: string;
  email: string;
  status: string;
  created_at: string;
}

const STATUSES = ["new", "contacted", "selected", "rejected"] as const;

export const Route = createFileRoute("/admin")({
  component: AdminPortal,
  head: () => ({
    meta: [
      { title: "Admin Portal – Campus Ambassador Leads" },
      {
        name: "description",
        content:
          "Private admin portal to review, track and manage Civil Engineering Campus Ambassador applications.",
      },
      { property: "og:title", content: "Admin Portal – Campus Ambassador Leads" },
      {
        property: "og:description",
        content: "Review and manage Campus Ambassador student applications.",
      },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function AdminPortal() {
  const [email, setEmail] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setEmail(session?.user.email ?? null);
    });
    void supabase.auth.getSession().then(({ data }) => {
      setEmail(data.session?.user.email ?? null);
      setChecking(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  if (checking) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading portal…
        </div>
      </Shell>
    );
  }

  if (!email) return <Shell><SignIn /></Shell>;

  if (email.toLowerCase() !== ADMIN_EMAIL) {
    return (
      <Shell>
        <div className="mx-auto max-w-md rounded-2xl border border-border bg-card p-8 text-center">
          <LockKeyhole className="mx-auto h-8 w-8 text-muted-foreground" />
          <h1 className="mt-4 text-lg font-bold text-foreground">Access restricted</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {email} is not the program admin account. Sign in with the admin email to
            view student leads.
          </p>
          <button
            onClick={() => void supabase.auth.signOut()}
            className="mt-6 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            Sign out
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <LeadsDashboard adminEmail={email} />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b border-border bg-background">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
          <span className="inline-flex items-center gap-2 text-sm font-extrabold tracking-tight text-foreground">
            <HardHat className="h-4 w-4 text-primary" /> Ambassador Admin
          </span>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-10">{children}</main>
    </div>
  );
}

function SignIn() {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [emailValue, setEmailValue] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    if (!emailValue.trim() || password.length < 6) {
      setError("Enter your email and a password of at least 6 characters.");
      return;
    }
    setBusy(true);
    if (mode === "signin") {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: emailValue.trim().toLowerCase(),
        password,
      });
      if (signInError) setError(signInError.message);
    } else {
      const { error: signUpError } = await supabase.auth.signUp({
        email: emailValue.trim().toLowerCase(),
        password,
        options: { emailRedirectTo: `${window.location.origin}/admin` },
      });
      if (signUpError) setError(signUpError.message);
      else setMessage("Account created. Check your inbox to confirm, then sign in.");
    }
    setBusy(false);
  };

  return (
    <div className="mx-auto max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <LockKeyhole className="h-5 w-5" />
      </span>
      <h1 className="mt-5 text-2xl font-extrabold text-foreground">Admin sign in</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Only the program admin account can view student leads.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-3" noValidate>
        <input
          type="email"
          value={emailValue}
          onChange={(event) => setEmailValue(event.target.value)}
          placeholder="Admin email"
          autoComplete="email"
          className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:border-primary"
        />
        <input
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="Password"
          autoComplete={mode === "signin" ? "current-password" : "new-password"}
          className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:border-primary"
        />
        {error ? (
          <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
            {error}
          </p>
        ) : null}
        {message ? (
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">
            {message}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-70"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {mode === "signin" ? "Sign in" : "Create admin account"}
        </button>
      </form>

      <button
        type="button"
        onClick={() => {
          setMode(mode === "signin" ? "signup" : "signin");
          setError(null);
          setMessage(null);
        }}
        className="mt-4 w-full text-center text-xs font-semibold text-primary underline-offset-4 hover:underline"
      >
        {mode === "signin"
          ? "First time? Create the admin account"
          : "Already have an account? Sign in"}
      </button>
    </div>
  );
}

function LeadsDashboard({ adminEmail }: { adminEmail: string }) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: loadError } = await supabase
      .from("applications")
      .select("*")
      .order("created_at", { ascending: false });
    if (loadError) setError(loadError.message);
    else {
      setError(null);
      setLeads((data ?? []) as Lead[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateStatus = async (id: string, status: string) => {
    setLeads((prev) => prev.map((lead) => (lead.id === id ? { ...lead, status } : lead)));
    await supabase.from("applications").update({ status }).eq("id", id);
  };

  const remove = async (id: string) => {
    setLeads((prev) => prev.filter((lead) => lead.id !== id));
    await supabase.from("applications").delete().eq("id", id);
  };

  const term = query.trim().toLowerCase();
  const filtered = term
    ? leads.filter((lead) =>
        [lead.full_name, lead.college_name, lead.branch, lead.email, lead.whatsapp]
          .join(" ")
          .toLowerCase()
          .includes(term),
      )
    : leads;

  const newCount = leads.filter((lead) => lead.status === "new").length;
  const selectedCount = leads.filter((lead) => lead.status === "selected").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground md:text-3xl">
            Student leads
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Signed in as {adminEmail}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void load()}
            className="inline-flex items-center gap-2 rounded-lg border border-input bg-background px-3 py-2 text-sm font-semibold text-foreground hover:bg-accent"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button
            onClick={() => void supabase.auth.signOut()}
            className="inline-flex items-center gap-2 rounded-lg bg-foreground px-3 py-2 text-sm font-semibold text-background"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Total applications" value={leads.length} />
        <StatCard label="New / unreviewed" value={newCount} />
        <StatCard label="Selected" value={selectedCount} />
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by name, college, branch, email or number"
          className="w-full rounded-xl border border-input bg-background py-3 pl-10 pr-4 text-sm outline-none focus:border-primary"
        />
      </div>

      {error ? (
        <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-card py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading leads…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card py-16 text-center">
          <Users className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-semibold text-foreground">No leads yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Applications submitted from the landing page will appear here instantly.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((lead) => (
            <article
              key={lead.id}
              className="rounded-2xl border border-border bg-card p-5 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-bold text-foreground">{lead.full_name}</h2>
                  <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Building2 className="h-3.5 w-3.5" /> {lead.college_name}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <HardHat className="h-3.5 w-3.5" /> {lead.branch} · {lead.year}
                    </span>
                  </p>
                  <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <a
                      href={`https://wa.me/91${lead.whatsapp}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                    >
                      <Phone className="h-3.5 w-3.5" /> +91 {lead.whatsapp}
                    </a>
                    <a
                      href={`mailto:${lead.email}`}
                      className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                    >
                      <Mail className="h-3.5 w-3.5" /> {lead.email}
                    </a>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={lead.status}
                    onChange={(event) => void updateStatus(lead.id, event.target.value)}
                    className="rounded-lg border border-input bg-background px-3 py-2 text-xs font-semibold capitalize outline-none focus:border-primary"
                  >
                    {STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => void remove(lead.id)}
                    aria-label={`Delete ${lead.full_name}`}
                    className="rounded-lg border border-input p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="mt-3 text-[11px] uppercase tracking-wide text-muted-foreground">
                Applied {new Date(lead.created_at).toLocaleString("en-IN")}
              </p>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 text-3xl font-extrabold text-foreground">{value}</p>
    </div>
  );
}
